import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  bookAppointment,
  getAvailability,
  getOnlineServices,
  getOnlineStaff,
  uploadAppointmentAttachment,
} from "../../api/clientPortalBooking";
import AuthRequiredCard from "../../components/clientPortal/AuthRequiredCard";
import InfoTooltip from "../../components/common/InfoTooltip";
import { useClientPortalAuth } from "../../context/ClientPortalAuthContext";
import { formatApiError } from "../../utils/formatApiError";

const stepTitles = [
  "Select Service",
  "Select Technician",
  "Select Date",
  "Select Time",
  "Confirm",
];

const ATTACHMENT_HELP_TEXT =
  "Upload nail art design ideas only (1 photo). JPG/PNG/WebP. Max 5 MB.";
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
];
const ALLOWED_ATTACHMENT_EXTS = [".jpg", ".jpeg", ".png", ".webp"];

const formatDateLabel = (value) => {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  return `${parts[0]}-${parts[1]}-${parts[2]}`;
};

const formatTimeLabel = (value) => {
  if (!value) return "";
  const parts = value.split("T");
  if (parts.length < 2) return value;
  const timePart = parts[1];
  return timePart.slice(0, 5);
};

const formatCurrency = (value) => {
  const amount = Number(value ?? 0);
  if (Number.isNaN(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const getServiceCategory = (service) => {
  const id = service?.category_id ?? service?.category?.id ?? null;
  const name =
    service?.category_name ??
    service?.category?.name ??
    service?.group_name ??
    service?.group ??
    "";
  if (!name && (id === null || id === undefined)) return null;
  return {
    id: String(id ?? name),
    name: String(name || "Other"),
  };
};

const defaultDateValue = () =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const toCalendarDate = (value) => {
  if (!value) return new Date();
  const [year, month, day] = String(value)
    .split("-")
    .map((part) => Number(part));
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const toIsoDateValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return defaultDateValue();
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function PortalBook() {
  const { salonId, setAssistantHandlers } = useOutletContext();
  const { token } = useClientPortalAuth();

  const [activeStep, setActiveStep] = useState(1);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const servicesRef = useRef([]);
  const selectedCategoryRef = useRef("all");
  const selectedServiceIdsRef = useRef([]);

  const [selectedServices, setSelectedServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [selectedDate, setSelectedDate] = useState(defaultDateValue());
  const [calendarViewDate, setCalendarViewDate] = useState(() =>
    toCalendarDate(defaultDateValue())
  );
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [tzName, setTzName] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [notes, setNotes] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [bookingSaving, setBookingSaving] = useState(false);
  const [navMessage, setNavMessage] = useState("");
  const navMessageTimerRef = useRef(null);
  const [assistantNotice, setAssistantNotice] = useState("");
  const assistantNoticeTimerRef = useRef(null);
  const [highlightedServiceId, setHighlightedServiceId] = useState(null);
  const stepRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
  };

  const categories = useMemo(() => {
    const map = new Map();
    services.forEach((service) => {
      const category = getServiceCategory(service);
      if (!category) return;
      const id = String(category.id ?? category.name);
      if (!map.has(id)) {
        map.set(id, { id, name: String(category.name || "Other") });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [services]);

  const filteredServices = useMemo(() => {
    const query = serviceSearch.trim().toLowerCase();
    let list = services;
    if (selectedCategoryId !== "all") {
      list = list.filter((service) => {
        const category = getServiceCategory(service);
        return category?.id === selectedCategoryId;
      });
    }
    if (!query) return list;
    return list.filter((service) => {
      const name = service?.name || "";
      const categoryName = getServiceCategory(service)?.name || "";
      const sku = service?.sku || "";
      const haystack = `${name} ${categoryName} ${sku}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [serviceSearch, services, selectedCategoryId]);

  const selectedServiceIds = useMemo(
    () => selectedServices.map((service) => service.id),
    [selectedServices]
  );

  const selectedServiceIdSet = useMemo(
    () => new Set(selectedServiceIds),
    [selectedServiceIds]
  );

  const selectedServicesNotInResults = useMemo(
    () =>
      selectedServices.filter(
        (service) => !filteredServices.some((candidate) => candidate.id === service.id)
      ),
    [filteredServices, selectedServices]
  );

  const displayedServices = useMemo(() => {
    const selected = [];
    const unselected = [];

    filteredServices.forEach((service) => {
      if (selectedServiceIdSet.has(service.id)) {
        selected.push(service);
      } else {
        unselected.push(service);
      }
    });

    return [...selected, ...unselected];
  }, [filteredServices, selectedServiceIdSet]);

  const selectedCalendarDate = useMemo(
    () => toCalendarDate(selectedDate),
    [selectedDate]
  );

  useEffect(() => () => {
    if (navMessageTimerRef.current) {
      clearTimeout(navMessageTimerRef.current);
    }
    if (assistantNoticeTimerRef.current) {
      clearTimeout(assistantNoticeTimerRef.current);
    }
  }, []);

  useEffect(() => {
    servicesRef.current = services;
  }, [services]);

  useEffect(() => {
    selectedCategoryRef.current = selectedCategoryId;
  }, [selectedCategoryId]);

  useEffect(() => {
    selectedServiceIdsRef.current = selectedServiceIds;
  }, [selectedServiceIds]);

  useEffect(() => {
    setCalendarViewDate(selectedCalendarDate);
  }, [selectedCalendarDate]);

  useEffect(() => {
    if (!attachmentFile) {
      setAttachmentPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(attachmentFile);
    setAttachmentPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [attachmentFile]);

  useEffect(() => {
    if (!token || !salonId) return;
    let active = true;
    const loadServices = async () => {
      setServicesLoading(true);
      setServicesError("");
      try {
        const data = await getOnlineServices(salonId, token);
        if (!active) return;
        setServices(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setServicesError(formatApiError(err));
      } finally {
        if (active) setServicesLoading(false);
      }
    };
    loadServices();
    return () => {
      active = false;
    };
  }, [salonId, token]);

  useEffect(() => {
    if (categories.length < 2) {
      if (selectedCategoryId !== "all") {
        setSelectedCategoryId("all");
      }
      return;
    }
    if (
      selectedCategoryId !== "all" &&
      !categories.some((category) => category.id === selectedCategoryId)
    ) {
      setSelectedCategoryId("all");
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    if (!selectedServiceIds.length || !token || !salonId) {
      setStaffList([]);
      setStaffError("");
      return;
    }
    let active = true;
    const loadStaff = async () => {
      setStaffLoading(true);
      setStaffError("");
      try {
        const data = await getOnlineStaff(
          salonId,
          { service_ids: selectedServiceIds },
          token
        );
        if (!active) return;
        setStaffList(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setStaffError(formatApiError(err));
      } finally {
        if (active) setStaffLoading(false);
      }
    };
    loadStaff();
    return () => {
      active = false;
    };
  }, [salonId, selectedServiceIds, token]);

  useEffect(() => {
    if (!selectedServiceIds.length || !selectedStaff || !selectedDate || !token) {
      setSlots([]);
      setSlotsError("");
      return;
    }
    let active = true;
    const loadAvailability = async () => {
      setSlotsLoading(true);
      setSlotsError("");
      try {
        const data = await getAvailability(
          salonId,
          {
            service_ids: selectedServiceIds,
            staff_id: selectedStaff.id,
            date: selectedDate,
          },
          token
        );
        if (!active) return;
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
        setTzName(data?.tz_name || "");
      } catch (err) {
        if (!active) return;
        setSlotsError(formatApiError(err));
        setSlots([]);
      } finally {
        if (active) setSlotsLoading(false);
      }
    };
    loadAvailability();
    return () => {
      active = false;
    };
  }, [salonId, selectedDate, selectedServiceIds, selectedStaff, token]);

  useEffect(() => {
    if (!selectedServiceIds.length) {
      setSelectedStaff(null);
      setSelectedSlot(null);
      return;
    }
    setSelectedStaff(null);
    setSelectedSlot(null);
    setActiveStep(2);
  }, [selectedServiceIds]);

  useEffect(() => {
    if (!selectedStaff) return;
    setSelectedSlot(null);
    setActiveStep(3);
  }, [selectedStaff]);

  useEffect(() => {
    if (!selectedDate) return;
    if (selectedServiceIds.length && selectedStaff) {
      setActiveStep(4);
    }
  }, [selectedDate, selectedServiceIds, selectedStaff]);

  useEffect(() => {
    if (!selectedSlot) return;
    setActiveStep(5);
  }, [selectedSlot]);

  const setNavMessageWithTimeout = (message) => {
    setNavMessage(message);
    if (navMessageTimerRef.current) {
      clearTimeout(navMessageTimerRef.current);
    }
    navMessageTimerRef.current = setTimeout(() => {
      setNavMessage("");
    }, 3000);
  };

  const setAssistantNoticeWithTimeout = (message) => {
    setAssistantNotice(message);
    if (assistantNoticeTimerRef.current) {
      clearTimeout(assistantNoticeTimerRef.current);
    }
    assistantNoticeTimerRef.current = setTimeout(() => {
      setAssistantNotice("");
    }, 3000);
  };

  const getStepGate = (step) => {
    if (step <= 1) return { ok: true };
    if (!selectedServiceIds.length) {
      return { ok: false, message: "Please select at least one service first." };
    }
    if (step === 2) return { ok: true };
    if (!selectedStaff) {
      return { ok: false, message: "Please select a technician first." };
    }
    if (step === 3) return { ok: true };
    if (!selectedDate) {
      return { ok: false, message: "Please select a date first." };
    }
    if (step === 4) return { ok: true };
    if (!selectedSlot) {
      return { ok: false, message: "Please select a time first." };
    }
    return { ok: true };
  };

  const scrollToStep = useCallback((step) => {
    const target = stepRefs?.[step]?.current;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const focusServiceCard = useCallback((serviceId) => {
    const card = document.querySelector(`[data-service-card="${serviceId}"]`);
    if (!card) return;
    card.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedServiceId(serviceId);
    setTimeout(() => setHighlightedServiceId(null), 1600);
  }, []);

  const toggleServiceSelection = useCallback((service) => {
    if (!service) return;
    setSelectedServices((current) => {
      const exists = current.some((item) => item.id === service.id);
      if (exists) {
        return current.filter((item) => item.id !== service.id);
      }
      return [...current, service];
    });
  }, []);

  const goToWeekOffset = useCallback((weeks) => {
    const base = selectedCalendarDate ? new Date(selectedCalendarDate) : new Date();
    const target = new Date(base);
    target.setDate(base.getDate() + weeks * 7);
    setSelectedDate(toIsoDateValue(target));
    setCalendarViewDate(target);
  }, [selectedCalendarDate]);

  const selectServiceFromAssistant = useCallback((serviceId, serviceName) => {
    const match = servicesRef.current.find(
      (service) => service.id === serviceId
    );
    if (!match) {
      setAssistantNoticeWithTimeout(
        "That service is not available online right now."
      );
      return;
    }
    const category = getServiceCategory(match);
    setSelectedCategoryId(category?.id || "all");
    setServiceSearch("");
    setSelectedServices((current) => {
      if (current.some((item) => item.id === match.id)) return current;
      return [...current, match];
    });
    setActiveStep(2);
    scrollToStep(2);
    setAssistantNoticeWithTimeout(`Selected: ${serviceName || match.name}`);
  }, [scrollToStep]);

  const viewServiceFromAssistant = useCallback((serviceId) => {
    const match = servicesRef.current.find(
      (service) => service.id === serviceId
    );
    if (match) {
      const category = getServiceCategory(match);
      setSelectedCategoryId(category?.id || "all");
      setServiceSearch("");
    }
    setActiveStep(1);
    scrollToStep(1);
    setTimeout(() => focusServiceCard(serviceId), 350);
  }, [focusServiceCard, scrollToStep]);

  const getAssistantContext = useCallback(() => {
    return {
      selected_category_id: selectedCategoryRef.current,
      selected_service_id: selectedServiceIdsRef.current[0] ?? null,
      selected_service_ids: selectedServiceIdsRef.current,
    };
  }, []);

  useEffect(() => {
    if (typeof setAssistantHandlers !== "function") return;
    setAssistantHandlers({
      selectService: selectServiceFromAssistant,
      viewService: viewServiceFromAssistant,
      getContext: getAssistantContext,
    });
    return () => setAssistantHandlers(null);
  }, [
    setAssistantHandlers,
    selectServiceFromAssistant,
    viewServiceFromAssistant,
    getAssistantContext,
  ]);

  const handleStepPillClick = (step) => {
    const gate = getStepGate(step);
    if (!gate.ok) {
      setNavMessageWithTimeout(gate.message);
      return;
    }
    setNavMessage("");
    setActiveStep(step);
    scrollToStep(step);
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    setAttachmentError("");
    if (!files.length) {
      setAttachmentFile(null);
      return;
    }
    if (files.length > 1) {
      setAttachmentError("Please upload only 1 photo.");
      event.target.value = "";
      return;
    }
    const file = files[0];
    const ext = file?.name
      ? `.${file.name.split(".").pop().toLowerCase()}`
      : "";
    const hasValidExt = ALLOWED_ATTACHMENT_EXTS.includes(ext);
    const hasValidType =
      !file?.type || ALLOWED_ATTACHMENT_TYPES.includes(file.type);
    if (!hasValidExt || !hasValidType) {
      setAttachmentError("Only JPG, PNG, or WebP images are allowed.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError("Image is too large. Max 5 MB.");
      event.target.value = "";
      return;
    }
    setAttachmentFile(file);
  };

  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    setAttachmentError("");
  };

  const handleBook = async () => {
    if (!selectedServiceIds.length || !selectedStaff || !selectedSlot || !token) return;
    if (attachmentError) {
      setBookingError("Please fix the photo upload error before booking.");
      return;
    }
    setBookingSaving(true);
    setBookingError("");
    setUploadNotice("");
    try {
      const payload = {
        salon_id: Number(salonId),
        service_ids: selectedServiceIds,
        staff_id: selectedStaff.id,
        start_time_local_iso: selectedSlot.start_time_local,
        notes: notes.trim() || null,
      };
      const data = await bookAppointment(payload, token);
      if (attachmentFile) {
        setUploadingAttachment(true);
        try {
          await uploadAppointmentAttachment(data.id, attachmentFile, token);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          setUploadNotice(
            typeof detail === "string" && detail
              ? detail
              : "Appointment booked. Photo upload failed - please try again or show your photo at your visit."
          );
        } finally {
          setUploadingAttachment(false);
        }
        setAttachmentFile(null);
        setAttachmentError("");
      }
      setBookingSuccess(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "APPOINTMENT_CONFLICT") {
        setBookingError("That time was just booked. Please pick another slot.");
      } else if (detail === "STAFF_TIME_OFF") {
        setBookingError("That time is no longer available. Please pick another slot.");
      } else {
        setBookingError(formatApiError(err));
      }
    } finally {
      setBookingSaving(false);
    }
  };

  const serviceSummary = selectedServices.length
    ? selectedServices
        .map((service) => `${service.name} (${service.duration_minutes || "?"} mins)`)
        .join(", ")
    : "Not selected";
  const totalServiceDuration = selectedServices.reduce(
    (sum, service) => sum + Number(service?.duration_minutes || 0),
    0
  );
  const totalServicePrice = selectedServices.reduce(
    (sum, service) => sum + Number(service?.base_price || 0),
    0
  );
  const hasHiddenSelectedPrice = selectedServices.some(
    (service) => service?.show_price_online === false || service?.base_price == null
  );
  const serviceDurationSummary = totalServiceDuration
    ? `${totalServiceDuration} min`
    : "-";
  const servicePriceSummary = selectedServices.length
    ? hasHiddenSelectedPrice
      ? "Price varies"
      : formatCurrency(totalServicePrice)
    : "-";
  const staffSummary = selectedStaff ? selectedStaff.display_name : "Not selected";
  const dateSummary = selectedDate ? formatDateLabel(selectedDate) : "Not selected";
  const timeSummary = selectedSlot
    ? `${formatTimeLabel(selectedSlot.start_time_local)} - ${formatTimeLabel(
        selectedSlot.end_time_local
      )}`
    : "Not selected";

  const showLoginPrompt = !token;

  if (showLoginPrompt) {
    return (
      <AuthRequiredCard
        title="Sign in to book online"
        subtitle="Please log in or register to book an appointment."
        salonId={salonId}
      />
    );
  }

  if (bookingSuccess) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <h2 className="text-xl md:text-2xl font-semibold text-emerald-800">
          Appointment booked!
        </h2>
        <p className="mt-2 text-sm md:text-base text-emerald-700">
          You are scheduled for{" "}
          {(selectedServices.length > 1 ? serviceSummary : bookingSuccess.service_name) ||
            serviceSummary}{" "}
          with{" "}
          {bookingSuccess.staff_name || staffSummary}.
        </p>
        {uploadNotice && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {uploadNotice}
          </div>
        )}
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link
            to={`/portal/salon/${salonId}/appointments`}
            className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800"
          >
            View my appointments
          </Link>
          <button
            type="button"
            onClick={() => {
              setBookingSuccess(null);
              setSelectedSlot(null);
              setActiveStep(4);
            }}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
          >
            Book another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
              Book your appointment
            </h2>
            <p className="mt-1 text-sm md:text-base text-slate-600">
              Follow the steps below to reserve your visit.
            </p>
          </div>
          {tzName && (
            <span className="rounded-full border border-slate-200 px-4 py-2 text-xs md:text-sm font-semibold text-slate-600">
              Times shown in {tzName}
            </span>
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs md:text-sm">
          {stepTitles.map((title, index) => {
            const stepNumber = index + 1;
            const isActive = activeStep === stepNumber;
            const isComplete = activeStep > stepNumber;
            const gate = getStepGate(stepNumber);
            const isBlocked = !gate.ok;
            return (
              <button
                key={title}
                type="button"
                onClick={() => handleStepPillClick(stepNumber)}
                aria-current={isActive ? "step" : undefined}
                aria-disabled={isBlocked}
                className={`rounded-full border px-3 py-2 text-center font-semibold transition ${
                  isActive
                    ? "border-rose-300 bg-rose-50 text-rose-700"
                    : isComplete
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-500"
                } ${isBlocked ? "opacity-50 cursor-not-allowed" : "hover:border-slate-300"}`}
              >
                Step {stepNumber}: {title}
              </button>
            );
          })}
        </div>
        {navMessage && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs md:text-sm text-slate-600">
            {navMessage}
          </div>
        )}
        {assistantNotice && (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs md:text-sm text-emerald-700">
            {assistantNotice}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5 2xl:grid-cols-12">
        <div
          id="step-1"
          ref={stepRefs[1]}
          className="rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 xl:col-span-3 2xl:col-span-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 1</h3>
              <p className="text-sm text-slate-600">Choose one or more services.</p>
            </div>
            <span className="text-xs text-slate-500">{serviceSummary}</span>
          </div>

          {servicesError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {servicesError}
            </div>
          )}
          {servicesLoading ? (
            <div className="mt-4 text-sm text-slate-500">Loading services...</div>
          ) : (
            <>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <span>Total duration</span>
                    <span className="font-semibold">{serviceDurationSummary}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span>Total price</span>
                    <span className="font-semibold">{servicePriceSummary}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end">
                {categories.length >= 2 && (
                  <div className="w-full md:max-w-xs">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Category
                    </label>
                    <div className="relative mt-2">
                      <select
                        value={selectedCategoryId}
                        onChange={(event) => setSelectedCategoryId(event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      >
                        <option value="all">All categories</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Search services
                  </label>
                  <div className="relative mt-2">
                    <input
                      type="text"
                      value={serviceSearch}
                      onChange={(event) => setServiceSearch(event.target.value)}
                      placeholder="Search services..."
                      className="h-11 w-full rounded-xl border border-slate-300 px-4 pr-10 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                    {serviceSearch.trim() && (
                      <button
                        type="button"
                        onClick={() => setServiceSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-600"
                        aria-label="Clear search"
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {selectedServicesNotInResults.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>
                    Selected outside current results:{" "}
                    {selectedServicesNotInResults.map((service) => service.name).join(", ")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedServices([])}
                    className="text-rose-600 hover:text-rose-700 hover:underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Services
                </span>
                <span className="text-xs text-slate-400">
                  {filteredServices.length} result{filteredServices.length === 1 ? "" : "s"}
                </span>
              </div>
              {selectedServices.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>
                    {selectedServices.length} service{selectedServices.length === 1 ? "" : "s"} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedServices([])}
                    className="text-rose-600 hover:text-rose-700 hover:underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
              <div className="mt-3 max-h-[32rem] overflow-y-auto pr-1">
                <div className="space-y-3">
                  {displayedServices.map((service) => {
                    const isSelected = selectedServiceIdSet.has(service.id);
                    const isHighlighted = highlightedServiceId === service.id;
                    const categoryName = getServiceCategory(service)?.name || "";
                    return (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => toggleServiceSelection(service)}
                        data-service-card={service.id}
                        className={`w-full rounded-2xl border p-4 text-left transition ${
                          isSelected
                            ? "border-rose-300 bg-rose-50 shadow-sm"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                        } ${isHighlighted ? "ring-2 ring-rose-300 animate-pulse" : ""}`}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                              isSelected
                                ? "border-rose-500 bg-rose-500"
                                : "border-slate-300 bg-white"
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && (
                              <span className="h-2 w-2 rounded-full bg-white" />
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="truncate text-base font-semibold text-slate-900">
                                  {service.name}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                                  <span>{service.duration_minutes || "?"} min</span>
                                  {categoryName && <span>{categoryName}</span>}
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-base font-semibold text-slate-700">
                                  {service.show_price_online === false ||
                                  service.base_price == null
                                    ? service.price_label || "Price varies"
                                    : formatCurrency(service.base_price)}
                                </div>
                                {isSelected && (
                                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-rose-600">
                                    Selected
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {!filteredServices.length && !servicesLoading && services.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                      No services match your search.
                    </div>
                  )}
                  {!services.length && !servicesLoading && (
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                      No online services are available yet.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div
          id="step-2"
          ref={stepRefs[2]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 xl:col-span-2 2xl:col-span-2 ${
            selectedServiceIds.length ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 2</h3>
              <p className="text-sm text-slate-600">Choose a technician.</p>
            </div>
            <span className="text-xs text-slate-500">{staffSummary}</span>
          </div>

          {staffError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {staffError}
            </div>
          )}
          {staffLoading ? (
            <div className="mt-4 text-sm text-slate-500">Loading technicians...</div>
          ) : (
            <div className="mt-4 max-h-[32rem] overflow-y-auto pr-1">
              <div className="space-y-3">
              {staffList.map((staff) => {
                const isSelected = selectedStaff?.id === staff.id;
                return (
                  <button
                    key={staff.id}
                    type="button"
                    onClick={() => selectedServiceIds.length && setSelectedStaff(staff)}
                    className={`w-full rounded-2xl border p-4 text-left transition ${
                      isSelected
                        ? "border-rose-300 bg-rose-50 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    disabled={!selectedServiceIds.length}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? "border-rose-500 bg-rose-500"
                            : "border-slate-300 bg-white"
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <span className="h-2 w-2 rounded-full bg-white" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-semibold text-slate-900">
                          {staff.display_name}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">Available online</div>
                        {isSelected && (
                          <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-rose-600">
                            Selected technician
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {!staffList.length && selectedServiceIds.length > 0 && !staffLoading && (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                  No technicians are available for this service combination.
                </div>
              )}
              </div>
            </div>
          )}
        </div>
        <div
          id="step-3"
          ref={stepRefs[3]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 xl:col-span-3 2xl:col-span-3 ${
            selectedStaff ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 3</h3>
              <p className="text-sm text-slate-600">Choose a date.</p>
            </div>
            <span className="text-xs text-slate-500">{dateSummary}</span>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 portal-book-calendar">
            <Calendar
              onChange={(value) => {
                const date = Array.isArray(value) ? value[0] : value;
                setSelectedDate(toIsoDateValue(date));
                setCalendarViewDate(date);
              }}
              value={selectedCalendarDate}
              activeStartDate={calendarViewDate}
              onActiveStartDateChange={({ activeStartDate }) => {
                if (activeStartDate) setCalendarViewDate(activeStartDate);
              }}
              calendarType="gregory"
              minDetail="month"
              maxDetail="month"
              selectRange={false}
              className="w-full"
              disabled={!selectedStaff}
              tileClassName={({ date, view }) => {
                if (view !== "month") return "";
                const isSelected =
                  selectedDate &&
                  toIsoDateValue(date) === selectedDate;
                if (isSelected) {
                  return "!bg-rose-500 !text-white rounded-2xl";
                }
                return "";
              }}
            />
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs text-slate-500">
                Pick a day that works for you.
              </span>
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  setSelectedDate(toIsoDateValue(today));
                  setCalendarViewDate(today);
                }}
                disabled={!selectedStaff}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
              >
                Today
              </button>
            </div>
            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold text-slate-700">
                Go to Week:
              </div>
              <div className="grid grid-cols-5 gap-2 text-xs">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((weeks) => (
                  <button
                    key={weeks}
                    type="button"
                    onClick={() => goToWeekOffset(weeks)}
                    disabled={!selectedStaff}
                    className="rounded-xl border border-slate-300 bg-white px-2 py-2 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
                  >
                    +{weeks}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          id="step-4"
          ref={stepRefs[4]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 xl:col-span-2 2xl:col-span-3 ${
            selectedStaff && selectedDate ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 4</h3>
              <p className="text-sm text-slate-600">Choose a time slot.</p>
            </div>
            <span className="text-xs text-slate-500">{timeSummary}</span>
          </div>

          {slotsError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {slotsError}
            </div>
          )}
          {slotsLoading ? (
            <div className="mt-4 text-sm text-slate-500">Loading times...</div>
          ) : (
            <div className="mt-4 max-h-[34rem] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
              {slots.map((slot) => {
                const isSelected =
                  selectedSlot?.start_time_local === slot.start_time_local;
                return (
                  <button
                    key={slot.start_time_local}
                    type="button"
                    onClick={() => setSelectedSlot(slot)}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                      isSelected
                        ? "border-rose-300 bg-rose-50 text-rose-700"
                        : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {formatTimeLabel(slot.start_time_local)}
                  </button>
                );
              })}
              {!slots.length && !slotsLoading && (
                <div className="col-span-full text-sm text-slate-500">
                  No availability for this date. Please choose another day.
                </div>
              )}
              </div>
            </div>
          )}
        </div>

        <div
          id="step-5"
          ref={stepRefs[5]}
          className={`rounded-2xl border border-slate-200 bg-white p-6 scroll-mt-28 xl:col-span-5 2xl:col-span-12 ${
            selectedSlot ? "" : "opacity-60"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Step 5</h3>
              <p className="text-sm text-slate-600">Review and confirm.</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Services</div>
              <div className="font-semibold text-slate-900">{serviceSummary}</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Technician</div>
              <div className="font-semibold text-slate-900">{staffSummary}</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Date</div>
              <div className="font-semibold text-slate-900">{dateSummary}</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="text-xs text-slate-500">Time</div>
              <div className="font-semibold text-slate-900">{timeSummary}</div>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-slate-600">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm"
              placeholder="Let us know anything helpful before your visit."
              disabled={!selectedSlot}
            />
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-slate-600">
                Nail design photo (optional)
              </label>
              <InfoTooltip text={ATTACHMENT_HELP_TEXT} label="Nail design photo" />
            </div>
            <p className="mt-1 text-xs text-slate-500">{ATTACHMENT_HELP_TEXT}</p>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleAttachmentChange}
              disabled={!selectedSlot || bookingSaving || uploadingAttachment}
              className="mt-2 block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800"
            />
            {attachmentError && (
              <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {attachmentError}
              </div>
            )}
            {attachmentFile && attachmentPreviewUrl && (
              <div className="mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <img
                  src={attachmentPreviewUrl}
                  alt="Nail design preview"
                  className="h-20 w-20 rounded-lg object-cover border border-slate-200 bg-white"
                />
                <div className="text-xs text-slate-600">
                  <div className="font-semibold text-slate-700">
                    {attachmentFile.name}
                  </div>
                  <div>{Math.round(attachmentFile.size / 1024)} KB</div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveAttachment}
                  className="ml-auto rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {bookingError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {bookingError}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleBook}
              disabled={!selectedSlot || bookingSaving}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {bookingSaving ? "Booking..." : "Book appointment"}
            </button>
            <button
              type="button"
              onClick={() => setSelectedSlot(null)}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
            >
              Change time
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
